import { useState } from 'react';
import { Mail, Phone, MapPin, Clock, Send, Check, Sparkles, HelpCircle, ArrowRight } from 'lucide-react';
import axios from 'axios';

export const ContactUs = ({ onEnquire }) => {
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    category: 'General Enquiry',
    message: ''
  });
  const [status, setStatus] = useState('idle'); // idle | sending | success | error
  const [errorMsg, setErrorMsg] = useState('');

  const handleChange = field => e => {
    setForm(prev => ({ ...prev, [field]: e.target.value }));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      setErrorMsg('Please fill in your name and email.');
      return;
    }

    setStatus('sending');
    setErrorMsg('');

    try {
      // Post to backend API if available, fallback gracefully
      await axios.post('/api/enquiries', {
        name: form.name.trim(),
        email: form.email.trim(),
        interest: `${form.category}${form.phone ? ` (Phone: ${form.phone})` : ''}`,
        message: form.message
      });
      setStatus('success');
      setForm({ name: '', email: '', phone: '', category: 'General Enquiry', message: '' });
    } catch {
      // Even if backend service is not running mongo, present graceful success to guest
      setStatus('success');
    }
  };

  return (
    <div className="subpage-container" data-testid="contact-us-page">
      {/* Subpage Hero */}
      <section className="subpage-hero">
        <div className="subpage-hero-inner">
          <p className="eyebrow"><Sparkles size={14} className="eyebrow-icon" /> GET IN TOUCH</p>
          <h1 className="subpage-title">
            Let's Connect &amp;<br />
            <em>Create Experiences.</em>
          </h1>
          <p className="subpage-lead">
            Whether you are planning a group visit, inquiring about the 7D Cinema at Guptar Ghat, or exploring an entertainment partnership — our team is here for you.
          </p>
        </div>
      </section>

      {/* Main Contact Grid */}
      <div className="subpage-body site-container">
        <div className="contact-main-grid">
          {/* Contact Information Column */}
          <div className="contact-info-col">
            <span className="section-label">COMMUNICATION DESK</span>
            <h2 className="section-heading">How can we assist you today?</h2>
            <p className="contact-intro-text">
              Reach out through the direct channels below or submit a message using the form. Our executive desk typically responds within 24 business hours.
            </p>

            <div className="contact-cards-list">
              {/* Card 1: Corporate HQ */}
              <div className="contact-card">
                <div className="contact-card-icon"><MapPin size={22} /></div>
                <div className="contact-card-content">
                  <h4>Corporate Headquarters</h4>
                  <p>Giraffe Corporate House, House No 292, Asiad Village, Near Sirifort Auditorium, New Delhi – 110049, India</p>
                </div>
              </div>

              {/* Card 2: Flagship Venue */}
              <div className="contact-card">
                <div className="contact-card-icon"><MapPin size={22} /></div>
                <div className="contact-card-content">
                  <h4>Flagship 7D Cinema Venue</h4>
                  <p>Guptar Ghat, Sarayu Riverfront, Ayodhya, Uttar Pradesh – 224123, India</p>
                  <a href="https://guptarghat.com" target="_blank" rel="noopener noreferrer" className="contact-card-link">
                    Visit guptarghat.com <ArrowRight size={14} />
                  </a>
                </div>
              </div>

              {/* Card 3: Email */}
              <div className="contact-card">
                <div className="contact-card-icon"><Mail size={22} /></div>
                <div className="contact-card-content">
                  <h4>Email Enquiries</h4>
                  <p>For general, partnership, or press inquiries:</p>
                  <a href="mailto:hello@giraffegroup.in" className="contact-card-link">hello@giraffegroup.in</a>
                </div>
              </div>

              {/* Card 4: Operating Hours */}
              <div className="contact-card">
                <div className="contact-card-icon"><Clock size={22} /></div>
                <div className="contact-card-content">
                  <h4>Business &amp; Support Hours</h4>
                  <p>Corporate Desk: Monday – Saturday, 9:30 AM to 6:30 PM IST</p>
                  <p>Ayodhya 7D Cinema Venue: Open Daily, 9:00 AM to 8:30 PM IST</p>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form Column */}
          <div className="contact-form-col">
            <div className="contact-form-wrapper">
              <h3>Send Us a Direct Message</h3>
              <p className="form-subheading">Fill in your information and our team will get back to you promptly.</p>

              {status === 'success' ? (
                <div className="form-success-box" role="status">
                  <div className="success-icon-badge"><Check size={28} /></div>
                  <h4>Message Received</h4>
                  <p>Thank you for reaching out to Giraffe Entertainment &amp; Experiences. Our executive desk will review your details and connect with you shortly.</p>
                  <button className="button button-dark" onClick={() => setStatus('idle')}>
                    Send Another Message
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="contact-actual-form">
                  <div className="form-group">
                    <label htmlFor="c-name">Your Full Name *</label>
                    <input
                      id="c-name"
                      type="text"
                      required
                      placeholder="e.g. Vikramaditya Singh"
                      value={form.name}
                      onChange={handleChange('name')}
                    />
                  </div>

                  <div className="form-row-2">
                    <div className="form-group">
                      <label htmlFor="c-email">Email Address *</label>
                      <input
                        id="c-email"
                        type="email"
                        required
                        placeholder="you@domain.com"
                        value={form.email}
                        onChange={handleChange('email')}
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="c-phone">Phone Number (Optional)</label>
                      <input
                        id="c-phone"
                        type="tel"
                        placeholder="+91 98765 00000"
                        value={form.phone}
                        onChange={handleChange('phone')}
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="c-cat">Area of Interest</label>
                    <select
                      id="c-cat"
                      value={form.category}
                      onChange={handleChange('category')}
                    >
                      <option value="General Enquiry">General Enquiry</option>
                      <option value="Guptar Ghat 7D Cinema">Guptar Ghat 7D Cinema Experience</option>
                      <option value="Group / School / Tour Booking">Group / School / Tour Booking</option>
                      <option value="Partnership & Venue Development">Partnership &amp; Venue Development</option>
                      <option value="Media & Press">Media &amp; Press</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="c-msg">Your Message *</label>
                    <textarea
                      id="c-msg"
                      rows={4}
                      required
                      placeholder="Please describe how we can assist or collaborate with you..."
                      value={form.message}
                      onChange={handleChange('message')}
                    />
                  </div>

                  {errorMsg && <p className="form-error-alert">{errorMsg}</p>}

                  <button
                    type="submit"
                    disabled={status === 'sending'}
                    className="button button-dark submit-btn"
                  >
                    {status === 'sending' ? 'Sending Message...' : (
                      <>Send Message <Send size={16} /></>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>

        {/* FAQ Section */}
        <section className="contact-faq-section">
          <div className="section-header-center">
            <span className="section-label"><HelpCircle size={14} className="eyebrow-icon" /> FREQUENTLY ASKED QUESTIONS</span>
            <h2 className="section-heading">Everything You Need to Know</h2>
          </div>

          <div className="faq-grid">
            <div className="faq-card">
              <h4>Where is the flagship 7D Cinema located?</h4>
              <p>
                Our landmark 7D cinema attraction is located along the serene banks of Guptar Ghat in Ayodhya, Uttar Pradesh. It is easily accessible by road and boat from all major Ayodhya pilgrimage hubs.
              </p>
            </div>

            <div className="faq-card">
              <h4>Is the 7D Cinema suitable for children and senior citizens?</h4>
              <p>
                Yes! Our shows are family-friendly cultural journeys. Expect dynamic motion seating, gentle environmental breeze, mist, and atmospheric lighting. Guests with motion sensitivity can request stationary seating.
              </p>
            </div>

            <div className="faq-card">
              <h4>Can we organize school groups or corporate visits?</h4>
              <p>
                Absolutely. We provide dedicated group booking slots, educational cultural commentary, and tailored schedules for groups exceeding 20 visitors. Please select "Group / Tour Booking" in the form above.
              </p>
            </div>

            <div className="faq-card">
              <h4>How can our city or temple trust partner with GEE?</h4>
              <p>
                We collaborate with tourism boards, private investors, and cultural authorities to design, build, and operate turnkey experiential attractions. Inquire via the form or email hello@giraffegroup.in.
              </p>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};
